/**
 * Enviar's WebRTC transport: opens a direct `RTCDataChannel` between two
 * browsers (same home or another network) with trickle-ICE signaling relayed by
 * `/api/p2p`, and delivers a `TransferChannel` from `@enviar/core` over which
 * the transfer protocol runs (framing/backpressure/resume/hash).
 *
 * It is a 1:1 adaptation of the already-proven host/guest dance of the local
 * multiplayer (duo): the only difference is that, on opening the channel, instead of a game
 * channel a generic `TransferChannel` is exposed. The transfer state
 * (the files) NEVER goes through the server: only SDP/ICE.
 */

import { dataChannelAdapter, isRecord, type TransferChannel } from "@enviar/core";
import {
  closeRoom,
  createRoom,
  fetchSignals,
  joinRoom,
  postSignal,
  SignalingError,
  type SignalingErrorKind,
} from "@/lib/p2p/signaling-client";

type TransferFailure = SignalingErrorKind | "peer-gone" | "webrtc-failed";

interface SessionCallbacks {
  /** Host: the code can now be shown/QR'd. */
  onCode?: (code: string, ttlSecs: number) => void;
  /** The channel opened: the transfer begins. */
  onChannel: (channel: TransferChannel) => void;
  /** The channel closed after opening (the peer left / finished). */
  onClosed: () => void;
  /** It never connected or failed irrecoverably. */
  onFailure: (kind: TransferFailure) => void;
}

export interface TransferSession {
  /** Aborts the session. Idempotent. */
  cancel: () => void;
}

const POLL_INTERVAL_MS = 700;
/**
 * How long the relay is tolerated to fail REPEATEDLY before declaring it down
 * (P23/CC-60). The polling must survive a network blip — a stray 503
 * or a wifi change cannot bring down a room that has been waiting for minutes —
 * but it CANNOT retry silently forever: without this ceiling, with
 * signaling down the sender was stuck at "Waiting for the other device…" and
 * the receiver at "Connecting…" ETERNALLY, without error, without retry and without
 * any hint that nothing was going to happen. Any successful poll resets the
 * counter, so it only triggers on a sustained failure.
 */
const SIGNALING_ERROR_GRACE_MS = 12_000;
const ICE_SERVERS: RTCIceServer[] = [{ urls: "stun:stun.l.google.com:19302" }];
const ROOM_TTL_SECS = 600;
const DATA_CHANNEL_LABEL = "enviar";

type SignalEnvelope =
  | { kind: "offer" | "answer"; sdp: string }
  | { kind: "ice"; candidate: RTCIceCandidateInit };

function toIceCandidate(value: unknown): RTCIceCandidateInit | null {
  if (!isRecord(value)) {
    return null;
  }
  const init: RTCIceCandidateInit = {};
  if (typeof value.candidate === "string") {
    init.candidate = value.candidate;
  }
  if (typeof value.sdpMid === "string") {
    init.sdpMid = value.sdpMid;
  }
  if (typeof value.sdpMLineIndex === "number") {
    init.sdpMLineIndex = value.sdpMLineIndex;
  }
  if (typeof value.usernameFragment === "string") {
    init.usernameFragment = value.usernameFragment;
  }
  return init;
}

function createSession(
  isHost: boolean,
  callbacks: SessionCallbacks,
): {
  session: TransferSession;
  run: (pc: RTCPeerConnection, code: string, token: string) => void;
  attachChannel: (pc: RTCPeerConnection, dc: RTCDataChannel, code: string, token: string) => void;
  isCancelled: () => boolean;
  fail: (kind: TransferFailure) => void;
} {
  let cancelled = false;
  let opened = false;
  let failed = false;
  let pollTimer: ReturnType<typeof setTimeout> | undefined;
  let peerConnection: RTCPeerConnection | undefined;
  let dataChannel: RTCDataChannel | undefined;

  const stopPolling = () => {
    if (pollTimer !== undefined) {
      clearTimeout(pollTimer);
      pollTimer = undefined;
    }
  };

  const teardown = () => {
    stopPolling();
    try {
      dataChannel?.close();
    } catch {
      /* already closed */
    }
    try {
      peerConnection?.close();
    } catch {
      /* already closed */
    }
  };

  const fail = (kind: TransferFailure) => {
    if (cancelled || failed || opened) {
      return;
    }
    failed = true;
    teardown();
    callbacks.onFailure(kind);
  };

  const session: TransferSession = {
    cancel: () => {
      if (cancelled) {
        return;
      }
      cancelled = true;
      teardown();
    },
  };

  const attachChannel = (
    pc: RTCPeerConnection,
    dc: RTCDataChannel,
    code: string,
    token: string,
  ) => {
    peerConnection = pc;
    dataChannel = dc;
    dc.binaryType = "arraybuffer";

    dc.addEventListener("open", () => {
      if (cancelled || failed) {
        return;
      }
      opened = true;
      stopPolling();
      void closeRoom(code, token);
      callbacks.onChannel(dataChannelAdapter(dc));
    });
    dc.addEventListener("close", () => {
      if (cancelled || failed) {
        return;
      }
      if (opened) {
        callbacks.onClosed();
      } else {
        fail("webrtc-failed");
      }
    });

    pc.addEventListener("connectionstatechange", () => {
      if (cancelled || failed) {
        return;
      }
      if (pc.connectionState === "failed") {
        if (opened) {
          callbacks.onClosed();
        } else {
          fail("webrtc-failed");
        }
      } else if (pc.connectionState === "disconnected") {
        setTimeout(() => {
          if (pc.connectionState === "disconnected" || pc.connectionState === "failed") {
            if (opened) {
              callbacks.onClosed();
            } else {
              fail("webrtc-failed");
            }
          }
        }, 3000);
      }
    });
  };

  const run = (pc: RTCPeerConnection, code: string, token: string) => {
    peerConnection = pc;
    let cursor = 0;
    /** Instant of the FIRST failure of the current streak (`undefined` = all good). */
    let failingSince: number | undefined;

    pc.addEventListener("icecandidate", (event: RTCPeerConnectionIceEvent) => {
      if (cancelled || failed || opened || !event.candidate) {
        return;
      }
      const envelope: SignalEnvelope = { kind: "ice", candidate: event.candidate.toJSON() };
      void postSignal(code, token, JSON.stringify(envelope)).catch(() => {
        /* room closed/expired with the channel opening */
      });
    });

    /** Applies ONE signaling message. Throws if the relay does not accept the answer. */
    const applySignal = async (payload: string): Promise<void> => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(payload);
      } catch {
        return; // garbage in room: discarded and proceed
      }
      if (!isRecord(parsed)) {
        return;
      }
      if (parsed.kind === "offer" && !isHost && typeof parsed.sdp === "string") {
        await pc.setRemoteDescription({ type: "offer", sdp: parsed.sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        const reply: SignalEnvelope = { kind: "answer", sdp: answer.sdp ?? "" };
        await postSignal(code, token, JSON.stringify(reply));
      } else if (parsed.kind === "answer" && isHost && typeof parsed.sdp === "string") {
        await pc.setRemoteDescription({ type: "answer", sdp: parsed.sdp });
      } else if (parsed.kind === "ice") {
        const candidate = toIceCandidate(parsed.candidate);
        if (candidate) {
          await pc.addIceCandidate(candidate).catch(() => {
            /* late/duplicate candidate */
          });
        }
      }
    };

    const poll = async () => {
      if (cancelled || failed || opened) {
        return;
      }
      try {
        const fetched = await fetchSignals(code, token, cursor);
        for (const { seq, payload } of fetched.messages) {
          // The cursor advances AFTER applying the message: if while answering the
          // offer the relay fails, the offer is read again on the next
          // poll. Advancing it before (as it was) a failed `postSignal`
          // left the connection dead forever even if the relay
          // recovered — nobody would send the answer again.
          await applySignal(payload);
          cursor = Math.max(cursor, seq);
        }
        failingSince = undefined; // relay responded: reset streak
      } catch (error) {
        if (cancelled || failed || opened) {
          return;
        }
        if (error instanceof SignalingError && error.kind === "not-found") {
          fail("not-found");
          return;
        }
        // Tolerable failure... until it stops being so: a relay that has been
        // `SIGNALING_ERROR_GRACE_MS` without responding is a failure, and it must be
        // SAID instead of continuing to poll silently (P23/CC-60).
        failingSince ??= Date.now();
        if (Date.now() - failingSince >= SIGNALING_ERROR_GRACE_MS) {
          fail(error instanceof SignalingError ? error.kind : "webrtc-failed");
          return;
        }
      }
      if (!cancelled && !failed && !opened) {
        pollTimer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
      }
    };

    void poll();
  };

  return { session, run, attachChannel, isCancelled: () => cancelled, fail };
}

/** Creates a room and waits for the receiver; delivers the `TransferChannel` on open. */
export function hostTransferSession(callbacks: SessionCallbacks): TransferSession {
  const { session, run, attachChannel, isCancelled, fail } = createSession(true, callbacks);

  void (async () => {
    let room;
    try {
      room = await createRoom(ROOM_TTL_SECS);
    } catch (error) {
      fail(error instanceof SignalingError ? error.kind : "unavailable");
      return;
    }
    if (isCancelled()) {
      void closeRoom(room.code, room.hostToken);
      return;
    }
    callbacks.onCode?.(room.code, room.ttlSecs);

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    const dc = pc.createDataChannel(DATA_CHANNEL_LABEL);
    attachChannel(pc, dc, room.code, room.hostToken);

    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const envelope: SignalEnvelope = { kind: "offer", sdp: offer.sdp ?? "" };
      await postSignal(room.code, room.hostToken, JSON.stringify(envelope));
    } catch (error) {
      fail(error instanceof SignalingError ? error.kind : "webrtc-failed");
      return;
    }
    run(pc, room.code, room.hostToken);
  })();

  return session;
}

/** Consumes a code and connects to the host; delivers the `TransferChannel`. */
export function joinTransferSession(code: string, callbacks: SessionCallbacks): TransferSession {
  const { session, run, attachChannel, isCancelled, fail } = createSession(false, callbacks);

  void (async () => {
    let token: string;
    try {
      token = await joinRoom(code);
    } catch (error) {
      fail(error instanceof SignalingError ? error.kind : "unavailable");
      return;
    }
    if (isCancelled()) {
      return;
    }

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pc.addEventListener("datachannel", (event: RTCDataChannelEvent) => {
      attachChannel(pc, event.channel, code, token);
    });
    pc.addEventListener("connectionstatechange", () => {
      if (pc.connectionState === "failed") {
        fail("webrtc-failed");
      }
    });
    run(pc, code, token);
  })();

  return session;
}
